import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import { Box } from "@mui/system";
import DialogTitle from "apps/web/src/componentLibrary/DialogTitle";
import DiscardChangesDialog from "apps/web/src/components/universal/DiscardChangesDialog";
import { GoalConfigurationTemplateTableItemFragment } from "apps/web/src/types";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { CreateGoalConfigurationTemplateDialogMode, goalConfigurationFormDefaultValues, GoalConfigurationTemplateFormSchema, GoalConfigurationTemplateFormType } from "../utils";
import { CreateGoalConfigurationTemplateDialogContent } from "./CreateGoalConfigurationTemplateDialogContent";

const DIALOG_WIDTH = "900px";
const DIALOG_HEIGHT = "650px";
const BUTTON_GAP = "8px";
interface CreateGoalConfigurationTemplateDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (goal: GoalConfigurationTemplateFormType) => void;
    selectedGoalConfigurationTemplate: GoalConfigurationTemplateTableItemFragment | null;
    mode: CreateGoalConfigurationTemplateDialogMode;
}

export const CreateGoalConfigurationTemplateDialog = ({
    open,
    onClose,
    onSubmit,
    selectedGoalConfigurationTemplate,
    mode
}: CreateGoalConfigurationTemplateDialogProps) => {
    const [discardChangesOpen, setDiscardChangesOpen] = useState(false);

    const form = useForm<GoalConfigurationTemplateFormType>({
        defaultValues: goalConfigurationFormDefaultValues(selectedGoalConfigurationTemplate),
        resolver: zodResolver(GoalConfigurationTemplateFormSchema),
        mode: "onChange",
    });

    const { isDirty } = form.formState;

    const handleClose = () => {
        if (isDirty) {
            return setDiscardChangesOpen(true);
        }
        onClose();
    };

    const getDialogTitle = (): string => {
        switch (mode) {
            case CreateGoalConfigurationTemplateDialogMode.Edit:
                return "Edit Goal";
            case CreateGoalConfigurationTemplateDialogMode.Duplicate:
                return "Duplicate Goal";
            case CreateGoalConfigurationTemplateDialogMode.View:
                return "View Goal";
            default:
                return "Create Goal";
        }
    };

    return (
        <Dialog
            PaperProps={{
                sx: { width: DIALOG_WIDTH, height: DIALOG_HEIGHT },
            }}
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle title={getDialogTitle()} onClose={handleClose} />
            <DialogContent sx={{ pt: 0 }}>
                <CreateGoalConfigurationTemplateDialogContent form={form} mode={mode} />
            </DialogContent>
            <DialogActions sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Box sx={{ display: "flex", gap: BUTTON_GAP }}>
                    <Button variant={mode !== CreateGoalConfigurationTemplateDialogMode.View ? "outlined" : "contained"} color="secondary" onClick={handleClose}>
                        {mode !== CreateGoalConfigurationTemplateDialogMode.View ? "Cancel" : "Return"}
                    </Button>
                    {mode !== CreateGoalConfigurationTemplateDialogMode.View && <Button onClick={form.handleSubmit(onSubmit)}>Save</Button>}
                </Box>
            </DialogActions>
            {
                discardChangesOpen && (
                    <DiscardChangesDialog
                        open={discardChangesOpen}
                        onClose={() => setDiscardChangesOpen(false)}
                        onDiscard={onClose} />
                )
            }
        </Dialog >
    );
};
